import { bigMath } from "../helpers";
import {
	ProcurementTransactionItemLike,
	ProcurementTransactionLike,
} from "./types";

export const calculatProcurementTransactionItem = (
	item: ProcurementTransactionItemLike,
) => {
	const subtotal = bigMath.mul(item.originalPrice, item.quantity);

	return {
		...item,
		subtotal: subtotal,
		tax: 0,
		deliveryFee: 0,
		otherFees: 0,
		cancelledAmount: 0,
		grossAmount: subtotal,
		netAmount: subtotal,
	} as ProcurementTransactionItemLike;
};

export const calculateProcurementTransaction = (
	transaction: ProcurementTransactionLike,
) => {
	const tax = transaction.tax ?? 0;
	const deliveryFee = transaction.deliveryFee ?? 0;
	const otherFees = transaction.otherFees ?? 0;
	const items = getCalculatedProcurementTransactionItem(
		transaction.items,
		tax,
		deliveryFee,
		otherFees,
	);

	const itemsSubtotal = items.reduce(
		(total, i) => bigMath.add(total, i.subtotal),
		0,
	);

	// cancelled item is precalculated already when add items calculated
	// for reference and is not used for transancation lv calculation.
	const cancelledItemsSubtotal = transaction.cancelledItems.reduce(
		(total, i) => bigMath.add(total, i.grossAmount),
		0,
	);

	const cancelledAmount = cancelledItemsSubtotal;
	const subtotal = itemsSubtotal;
	const fees = bigMath.add(tax, deliveryFee, otherFees);
	const grossAmount = bigMath.add(subtotal, fees);

	return {
		...transaction,
		items: items as unknown as ProcurementTransactionItemLike[],
		subtotal: subtotal,
		cancelledAmount: cancelledAmount,
		grossAmount: grossAmount,
		netAmount: grossAmount,
	} as ProcurementTransactionLike;
};

export const getCalculatedProcurementTransactionItem = (
	items: ProcurementTransactionItemLike[],
	tax: number,
	deliveryFee: number,
	otherFees: number,
) => {
	let remainingTax = tax;
	let remainingDeliveryFee = deliveryFee;
	let remainingOtherFees = otherFees;

	const itemsSubtotal = items.reduce(
		(total, i) => bigMath.add(total, i.subtotal),
		0,
	);

	return items
		.sort(
			(a: { subtotal: number }, b: { subtotal: number }) =>
				a.subtotal - b.subtotal,
		)
		.map((item, i) => {
			const isLastIndex = items.length === i + 1;
			if (isLastIndex) {
				// last index push remaining into the smallest subtotal
				const grandTotal = bigMath.add(
					item.subtotal,
					remainingTax,
					remainingDeliveryFee,
					remainingOtherFees,
				);
				return {
					...item,
					tax: remainingTax,
					deliveryFee: remainingDeliveryFee,
					otherFees: remainingOtherFees,
					netAmount: grandTotal,
					grossAmount: grandTotal,
				};
			} else {
				// not last index calculate by percentage
				const percentage = bigMath.div(item.subtotal, itemsSubtotal);
				const itemTax = tax !== 0 ? bigMath.mul(tax, percentage) : 0;
				const itemDeliveryFee =
					deliveryFee !== 0 ? bigMath.mul(deliveryFee, percentage) : 0;
				const itemOtherFees =
					otherFees !== 0 ? bigMath.mul(otherFees, percentage) : 0;
				remainingTax = bigMath.sub(remainingTax, itemTax);
				remainingDeliveryFee = bigMath.sub(
					remainingDeliveryFee,
					itemDeliveryFee,
				);
				remainingOtherFees = bigMath.sub(remainingOtherFees, itemOtherFees);
				const grandTotal = bigMath.add(
					item.subtotal,
					itemTax,
					itemDeliveryFee,
					itemOtherFees,
				);
				return {
					...item,
					tax: itemTax,
					deliveryFee: itemDeliveryFee,
					otherFees: itemOtherFees,
					netAmount: grandTotal,
					grossAmount: grandTotal,
				};
			}
		});
};
